import type { ImgHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { usePlatformUserCount } from "@/hooks/use-platform-user-count";
import { trackEvent } from "@/lib/analytics";
import { LocaleLink as Link } from "./LocaleLink";

// LDT-718 · Cada imagen del hero en AVIF, con el WebP de respaldo: 188 KB →
// 95 KB, y todas se descargan antes de que se pinte la pantalla de familias
// (el LCP de la portada). `display: contents` deja el <img> como hijo directo
// de la composición, que es la que lo coloca por clase.
function HeroImg({ name, ...img }: { name: string } & ImgHTMLAttributes<HTMLImageElement>) {
  return (
    <picture style={{ display: "contents" }}>
      <source type="image/avif" srcSet={`/images/home/${name}.avif`} />
      <img src={`/images/home/${name}.webp`} {...img} />
    </picture>
  );
}

// Ya no recibe `onScrollTo`: el CTA llevaba al bloque de packs de la home,
// que se movió a /pricing. Un scroll a una sección inexistente no falla —
// simplemente no hace nada, que es peor que un error visible.
export default function Hero() {
  const { t, i18n } = useTranslation();
  const userCount = usePlatformUserCount();

  return (
    <section className="playium-hero">
      <div className="playium-hero__inner">
        <div className="playium-hero__copy">
          <span className="playium-hero__badge">
            <Sparkles aria-hidden="true" />
            {t("hero.badge")}
          </span>

          <h1 className="playium-hero__title playium-heading">
            {t("hero.title")}
          </h1>

          <p className="playium-hero__subtitle">{t("hero.subtitle")}</p>

          <div className="playium-hero__ctas">
            <Link
              to="/pricing"
              className="playium-hero__cta-primary"
              onClick={() => trackEvent("cta_hero_pricing_clicked", { location: "hub_hero" })}
            >
              {t("hero.ctaPrimary")}
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>

          {userCount !== null && (
            <p className="playium-hero__trust">
              {t("hero.trust", { users: userCount.toLocaleString(i18n.language) })}
            </p>
          )}
        </div>

        <div className="playium-hero__visual">
          <div className="playium-hero__composition" aria-hidden="true">
            {/* Capa de fondo: gear y lego asoman por detrás del hipopótamo. */}
            <HeroImg
              name="GEAR_CUT_3"
              alt=""
              className="ph-comp ph-comp--gear"
              loading="eager"
            />
            <HeroImg
              name="LEGO_2"
              alt=""
              className="ph-comp ph-comp--lego-lavender"
              loading="eager"
            />

            {/* Capa media: el hipopótamo, ancla derecha.
                LDT-717 · Sin `fetchpriority="high"`: le disputaba la red a la
                pantalla de familias de abajo, que es el LCP de la portada.
                Solo el LCP va con prioridad alta. */}
            <HeroImg
              name="HIPOPOTAMO_1"
              alt=""
              className="ph-comp ph-comp--hippo"
              loading="eager"
            />

            {/* Capa principal: pantalla familias (incluye la badge "Familias"
                con las líneas punteadas integrada en el PNG). */}
            <HeroImg
              name="PANTALLA_FAMILIAS"
              alt=""
              className="ph-comp ph-comp--screen"
              loading="eager"
              decoding="sync"
              {...({ fetchpriority: "high" } as Record<string, string>)}
            />

            {/* Capa frente: badges flotantes. */}
            <HeroImg
              name="QR_PASS"
              alt=""
              className="ph-comp ph-comp--qr"
              loading="eager"
            />
            <HeroImg
              name="BONO_DISPONIBLE"
              alt=""
              className="ph-comp ph-comp--bono"
              loading="eager"
            />
            <HeroImg
              name="PROXIMA_RESERVA_1"
              alt=""
              className="ph-comp ph-comp--reserva"
              loading="eager"
            />

            {/* Suelo: lego mint + pelotas pequeñas. */}
            <HeroImg
              name="LEGO_1"
              alt=""
              className="ph-comp ph-comp--lego-mint"
              loading="eager"
            />
            <HeroImg
              name="PELOTA_1"
              alt=""
              className="ph-comp ph-comp--ball-mint"
              loading="eager"
            />
            <HeroImg
              name="PELOTA_2"
              alt=""
              className="ph-comp ph-comp--ball-orange"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
